import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@devhop/ui";
import { Link, useSearch } from "@tanstack/react-router";
import {
  ReportSummaryStatCard,
  ReportSummaryStatGrid,
  reportSummaryCount,
} from "@/modules/reports/presentation/ui/ReportSummaryStatCard";
import {
  AlertCircleIcon,
  ClipboardListIcon,
  ClockIcon,
  DownloadIcon,
  ExternalLinkIcon,
  HashIcon,
  TrendingUpIcon,
} from "lucide-react";
import { useCallback, useMemo } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { usePerformanceRequestReportQuery } from "../api/queries";
import {
  PerformanceReportDateFilter,
  type PerformanceReportSearch,
  usePerformanceReportDates,
} from "../ui/PerformanceReportDateFilter";
import {
  escapeCsvCell,
  formatPerformanceDateTime,
  PerformanceReportsNav,
} from "../ui/PerformanceReportsNav";

const STATUS_LABELS = {
  new: "ຮັບຄຳຂໍໃໝ່",
  negotiating: "ກຳລັງເຈລະຈາ",
  pending_quote: "ລໍອອກໃບສະເໜີລາຄາ",
  won: "ຢືນຢັນແລ້ວ",
  lost: "ບໍ່ໄດ້ຮັບຈ້າງ",
  cancelled: "ຍົກເລີກ",
} as const;

const CHANNEL_LABELS = {
  phone: "ໂທ",
  line: "LINE",
  facebook: "Facebook",
  walk_in: "ເຂົ້າມາ",
  referral: "ແນະນຳ",
  other: "ອື່ນໆ",
} as const;

export function PerformanceRequestReportPage() {
  const search = useSearch({
    from: "/app/performance/reports/requests",
  }) as PerformanceReportSearch;

  const { dateFrom, dateTo } = usePerformanceReportDates(search);
  const offset = search.offset ?? 0;
  const limit = 20;

  const query = useMemo(
    () => ({
      dateFrom,
      dateTo,
      status: search.status as
        | "new"
        | "negotiating"
        | "pending_quote"
        | "won"
        | "lost"
        | "cancelled"
        | undefined,
      offset,
      limit,
    }),
    [dateFrom, dateTo, search.status, offset],
  );

  const { data, isLoading } = usePerformanceRequestReportQuery(query);
  const rows = data?.data ?? [];
  const summary = data?.summary;
  const total = data?.total ?? 0;

  const exportCsv = useCallback(() => {
    const header = "ຫົວຂໍ້,ຜູ້ຕິດຕໍ່,ຊ່ອງທາງ,ສະຖານະ,ວັນທີຮັບຄຳຂໍ,ວັນທີກາຍເປັນງານ,ໃຊ້ເວລາ(ວັນ)";
    const csvRows = rows.map((row) =>
      [
        escapeCsvCell(row.title),
        escapeCsvCell(row.contactName),
        escapeCsvCell(row.channel),
        escapeCsvCell(
          STATUS_LABELS[row.status as keyof typeof STATUS_LABELS] ?? row.status,
        ),
        escapeCsvCell(formatPerformanceDateTime(row.createdAt)),
        escapeCsvCell(
          row.convertedAt ? formatPerformanceDateTime(row.convertedAt) : "",
        ),
        escapeCsvCell(row.daysToConvert ?? ""),
      ].join(","),
    );
    const csv = [header, ...csvRows].join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `performance-requests-report-${dateFrom}-${dateTo}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [rows, dateFrom, dateTo]);

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ງານສະແດງ", to: "/app/performance/events" },
            { label: "ລາຍງານງານສະແດງ", to: "/app/performance/reports" },
            { label: "ຄຳຂໍ" },
          ]}
        />
      </Header>

      <Main>
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-bold text-2xl">ລາຍງານຄຳຂໍລູກຄ້າ</h1>
            <p className="mt-1 text-muted-foreground text-sm">
              ຕິດຕາມຄຳຂໍຕັ້ງແຕ່ຮັບເລື່ອງ ຈົນກາຍເປັນງານສະແດງ
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={exportCsv}
            disabled={rows.length === 0}
          >
            <DownloadIcon className="mr-2 size-4" />
            ດາວໂຫລດ CSV
          </Button>
        </div>

        <PerformanceReportsNav />

        <div className="mb-6 flex flex-col rounded-xl border bg-card pt-2">
          <PerformanceReportDateFilter
            routeFrom="/app/performance/reports/requests"
            showRequestStatus
          />
        </div>

        <ReportSummaryStatGrid columns={4}>
          <ReportSummaryStatCard
            title="ຄຳຂໍທັງໝົດ"
            icon={ClipboardListIcon}
            tone="attendance"
            isLoading={isLoading}
            value={reportSummaryCount(summary?.total ?? 0)}
          />
          <ReportSummaryStatCard
            title="ອັດຕາກາຍເປັນງານ"
            icon={TrendingUpIcon}
            tone="revenue"
            isLoading={isLoading}
            value={`${summary?.conversionRate ?? 0}%`}
          />
          <ReportSummaryStatCard
            title="ໃຊ້ເວລາກ່ອນເປັນງານ"
            icon={ClockIcon}
            tone="packages"
            isLoading={isLoading}
            value={
              summary?.avgDaysToConvert != null
                ? `${summary.avgDaysToConvert} ວັນ`
                : "—"
            }
          />
          <ReportSummaryStatCard
            title="ຄຳຂໍຄ້າງດຳເນີນການ"
            icon={AlertCircleIcon}
            tone="refunds"
            isLoading={isLoading}
            value={reportSummaryCount(summary?.openOverdueCount ?? 0)}
          />
        </ReportSummaryStatGrid>

        {summary ? (
          <ReportSummaryStatGrid columns={6}>
            {(
              [
                ["new", summary.byStatus.new],
                ["negotiating", summary.byStatus.negotiating],
                ["pending_quote", summary.byStatus.pendingQuote],
                ["won", summary.byStatus.won],
                ["lost", summary.byStatus.lost],
                ["cancelled", summary.byStatus.cancelled],
              ] as const
            ).map(([key, count]) => (
              <ReportSummaryStatCard
                key={key}
                title={STATUS_LABELS[key]}
                icon={HashIcon}
                tone="students"
                isLoading={false}
                value={reportSummaryCount(count)}
              />
            ))}
          </ReportSummaryStatGrid>
        ) : null}

        <Card>
          <CardHeader>
            <CardTitle>ຕາຕະລາງຄຳຂໍ ({total})</CardTitle>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            {isLoading ? (
              <p className="text-muted-foreground text-sm">ກຳລັງໂຫລດ...</p>
            ) : rows.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                ບໍ່ມີຂໍ້ມູນໃນຊ່ວງວັນທີທີ່ເລືອກ
              </p>
            ) : (
              <table className="w-full min-w-[900px] text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="pr-4 pb-2 font-medium">ຫົວຂໍ້</th>
                    <th className="pr-4 pb-2 font-medium">ຜູ້ຕິດຕໍ່</th>
                    <th className="pr-4 pb-2 font-medium">ຊ່ອງທາງ</th>
                    <th className="pr-4 pb-2 font-medium">ສະຖານະ</th>
                    <th className="pr-4 pb-2 font-medium">ວັນທີຮັບຄຳຂໍ</th>
                    <th className="pr-4 pb-2 font-medium">ວັນທີກາຍເປັນງານ</th>
                    <th className="pb-2 text-right font-medium">ໃຊ້ເວລາ (ວັນ)</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => (
                    <tr key={row.id} className="border-b last:border-0">
                      <td className="py-3 pr-4">
                        <Link
                          to="/app/performance/requests/$id"
                          params={{ id: row.id }}
                          className="inline-flex items-center gap-1 font-medium hover:underline"
                        >
                          {row.title}
                          <ExternalLinkIcon className="size-3" />
                        </Link>
                      </td>
                      <td className="py-3 pr-4">{row.contactName}</td>
                      <td className="py-3 pr-4">
                        {CHANNEL_LABELS[
                          row.channel as keyof typeof CHANNEL_LABELS
                        ] ?? row.channel}
                      </td>
                      <td className="py-3 pr-4">
                        <Badge variant="outline">
                          {STATUS_LABELS[
                            row.status as keyof typeof STATUS_LABELS
                          ] ?? row.status}
                        </Badge>
                      </td>
                      <td className="whitespace-nowrap py-3 pr-4">
                        {formatPerformanceDateTime(row.createdAt)}
                      </td>
                      <td className="whitespace-nowrap py-3 pr-4">
                        {formatPerformanceDateTime(row.convertedAt)}
                      </td>
                      <td className="py-3 text-right">
                        {row.daysToConvert != null
                          ? `${row.daysToConvert} ວັນ`
                          : "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      </Main>
    </>
  );
}
